import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import * as XLSX from "xlsx-js-style";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import RowsPerPageSelector from "../common/RowsPerPageSelector";
import Pagination from "../common/Pagination";
import { styles } from "../../styles/pages/Contact/ContactTablestyle";
import Loader from "../common/Loader";
import { ReactComponent as DownloadIcon } from "../../icons/ExcelDownloadIcon.svg";
import { Tooltip } from "../../components/common/Tooltip";
import { DownArrow, UpArrow } from "../svg/Icons";

// Define API response structure
interface Service {
  service_name: string;
  price: string;
  service_GST: string;
  discount: string;
  service_total: string;
  description: string | null;
  discount_type: number; // 1 for percentage, 2 for fixed amount
}

interface Product {
  product_id: number;
  product_name: string;
  price: string;
  quantity: number;
  IGST: string;
  CGST: string;
  SGST: string;
  discount: string;
  product_total: string;
  description: string | null;
}

interface Quotation {
  quotation_number: string;
  quotation_type: number; // 1 for Product, 2 for Service
  quotation_subject: string;
  quotation_status: string;
  valid_until: string;
  terms_and_conditions: string | null;
  total: string;
  group_total: string;
  services?: Service[];
  products?: Product[];
  from_company_name?: string;
  from_address?: string;
  from_phone?: string;
  from_email?: string;
  to_client_name?: string;
  to_address?: string;
  to_email?: string;
}

interface Opportunity {
  opportunity_name: string;
  expected_close_date: string;
  opp_amount: string;
  probability: number;
}

interface APIContact {
  name: string;
  email: string;
  phone: string;
  company: string | null;
  status: string;
  converted_by: string;
  quotations: Quotation[];
  opportunities: Opportunity[];
}

interface Contact {
  id: number;
  name: string;
  email: string;
  phone: string;
  company: string;
  status: string;
  converted_by: string;
  quotations: Quotation[];
  opportunities: Opportunity[];
}

const ContactTable: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [sortColumn, setSortColumn] = useState<keyof Contact | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const getAuthToken = (): string | null => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      toast.error("Authentication error! Please log in.");
      console.error("No auth token found.");
    }
    return token;
  };

  // Memoize fetchContacts to prevent unnecessary re-renders
  const fetchContacts = useCallback(async () => {
    setLoading(true);
    setError(null);

    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/contact/list`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.status === 200 && Array.isArray(response.data.data)) {
        const formattedContacts: Contact[] = response.data.data.map(
          (contact: APIContact, index: number) => ({
            id: index + 1,
            name: contact.name,
            email: contact.email,
            phone: contact.phone,
            company: contact.company || "N/A",
            status: contact.status,
            converted_by: contact.converted_by,
            quotations: contact.quotations || [],
            opportunities: contact.opportunities || [],
          })
        );
        setContacts(formattedContacts);
      } else {
        setContacts([]);
        setError("No contacts found or invalid response.");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage]);

  // Filter and sort contacts
  const filteredContacts = contacts.filter((contact) =>
    Object.values(contact)
      .filter((value) => typeof value === "string")
      .some((value) => value.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const sortedContacts = [...filteredContacts].sort((a, b) => {
    if (!sortColumn) return 0;
    const valA = a[sortColumn];
    const valB = b[sortColumn];
    if (typeof valA === "string" && typeof valB === "string") {
      return sortOrder === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    }
    if (typeof valA === "number" && typeof valB === "number") {
      return sortOrder === "asc" ? valA - valB : valB - valA;
    }
    return 0;
  });

  const displayedContacts = sortedContacts.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const totalPages = Math.ceil(filteredContacts.length / rowsPerPage);

  const handleSort = (column: keyof Contact) => {
    setSortColumn(column);
    setSortOrder(sortColumn === column && sortOrder === "asc" ? "desc" : "asc");
  };

  const handleDownload = () => {
    const dataToDownload = searchTerm
      ? filteredContacts
      : sortColumn
        ? sortedContacts
        : contacts;

    if (!dataToDownload.length) {
      toast.error("No data available to download.");
      return;
    }

    const formattedData = dataToDownload.map((contact) => ({
      Name: contact.name,
      Email: contact.email,
      MobileNumber: contact.phone,
      Company: contact.company,
      Status: contact.status,
      "Converted By": contact.converted_by,
    }));

    const headers = Object.keys(formattedData[0]);
    const worksheet = XLSX.utils.aoa_to_sheet([]);

    // 1. Add title in A1
    XLSX.utils.sheet_add_aoa(worksheet, [["Contact Data List"]], {
      origin: "A1",
    });

    // 2. Merge title across all columns
    worksheet["!merges"] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: headers.length - 1 },
      },
    ];

    // 3. Style the title cell
    worksheet["A1"].s = {
      font: { bold: true, sz: 14 },
      alignment: { horizontal: "center", vertical: "center" },
    };

    // 4. Add headers in row 2
    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A2" });

    // 5. Bold header cells
    headers.forEach((_, i) => {
      const col = String.fromCharCode(65 + i); // A, B, C...
      const cell = `${col}2`;
      if (worksheet[cell]) {
        worksheet[cell].s = {
          font: { bold: true },
        };
      }
    });

    // 6. Add data starting from row 3
    XLSX.utils.sheet_add_json(worksheet, formattedData, {
      origin: "A3",
      skipHeader: true,
    });

    // 7. Auto-size columns
    worksheet["!cols"] = headers.map((key) => ({
      wch:
        Math.max(
          key.length,
          ...formattedData.map(
            (row) =>
              (row[key as keyof typeof row] as string)?.toString().length || 0
          ),
          "Contact Data List".length
        ) + 2,
    }));

    // 8. Export to file
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Contacts");
    XLSX.writeFile(
      workbook,
      searchTerm ? `ContactData_${searchTerm}.xlsx` : "ContactData.xlsx"
    );
  };

  const handleViewClick = (contact: Contact) => {
    navigate("/contact-details", { state: { contactId: contact.id } });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Contact List</h3>
      </div>
      <div className={styles.tableCard}>
        <div className={styles.headerControls}>
          <RowsPerPageSelector
            rowsPerPage={rowsPerPage}
            setRowsPerPage={(val) => {
              setRowsPerPage(val);
              setCurrentPage(1);
            }}
          />
          <div className={styles.controlsInner}>
            <div className={styles.searchContainer}>
              <Search className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search..."
                className={styles.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button className={styles.downloadButton} onClick={handleDownload}>
              Download
              <DownloadIcon className={styles.downloadIcon} />
            </button>
          </div>
        </div>

        {error && <p className={styles.errorText}>{error}</p>}
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr className={styles.theadRow}>
                <th
                  className={styles.thName}
                  onClick={() => handleSort("name")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Name</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thEmail}
                  onClick={() => handleSort("email")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Email</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thMobileNumber}
                  onClick={() => handleSort("phone")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Mobile Number</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thRole}
                  onClick={() => handleSort("company")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Company</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thRole}
                  onClick={() => handleSort("status")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Status</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thRole}
                  onClick={() => handleSort("converted_by")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Converted By</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th className={styles.thAction}>
                  <p className={styles.thText}>Action</p>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr className={styles.loadingRow}>
                  <td colSpan={7} className="text-center py-4">
                    <Loader />
                  </td>
                </tr>
              ) : displayedContacts.length ? (
                displayedContacts.map((contact) => (
                  <tr key={contact.id} className={styles.tbodyRow}>
                    <td className={styles.tdName}>{contact.name}</td>
                    <td className={styles.tdEmail}>{contact.email}</td>
                    <td className={styles.tdMobileNumber}>{contact.phone}</td>
                    <td className={styles.tdRole}>{contact.company}</td>
                    <td className={styles.tdRole}>{contact.status}</td>
                    <td className={styles.tdRole}>{contact.converted_by}</td>
                    <td className={styles.tdAction}>
                      <div className={styles.actionContainer}>
                        <Tooltip content="View" bgColor="bg-green-600">
                          <button
                            className={styles.viewButton}
                            onClick={() => handleViewClick(contact)}
                          >
                            <FontAwesomeIcon
                              icon={faEye}
                              className="text-white text-sm"
                            />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr className={styles.loadingRow}>
                  <td colSpan={7} className={styles.noDataText}>
                    No records match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          filteredLength={filteredContacts.length}
          handlePageChange={setCurrentPage}
        />
      </div>
    </div>
  );
};

export default ContactTable;